import React from 'react';
import {
  Title,
  Group,
  Button,
  Text,
  Badge,
  Stack,
  Grid,
  Card,
  Avatar,
  Divider,
  ActionIcon,
  LoadingOverlay,
  Alert,
  SimpleGrid,
  Progress,
  Table,
} from '@mantine/core';
import {
  IconEdit,
  IconArrowLeft,
  IconSchool,
  IconMapPin,
  IconPhone,
  IconMail,
  IconCalendar,
  IconUsers,
  IconCamera,
  IconActivity,
  IconAlertTriangle,
  IconCheck,
  IconClock,
} from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSchool } from '../../hooks/useSchools';
import { useStudents } from '../../hooks/useStudents';
import { useCameras } from '../../hooks/useCameras';
import type { Camera } from '../../types/api';

const SchoolDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const {
    data: school,
    isLoading: schoolLoading,
    error: schoolError,
  } = useSchool(id || '');

  const {
    data: studentsData,
    isLoading: studentsLoading,
  } = useStudents({
    per_page: 5, // Just for recent students
  });

  const {
    data: camerasData,
    isLoading: camerasLoading,
  } = useCameras({
    per_page: 10,
  });

  const students = studentsData?.students || [];
  const cameras = camerasData?.cameras || [];

  const handleEdit = () => {
    navigate(`/admin/schools/${id}/edit`);
  };

  const handleBack = () => {
    navigate('/admin/schools');
  };

  const getSchoolTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      kindergarten: 'blue',
      preschool: 'green',
      daycare: 'orange',
      nursery: 'purple',
    };
    return colors[type] || 'gray';
  };

  const getCameraStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'green',
      inactive: 'red',
      maintenance: 'yellow',
      error: 'red',
    };
    return colors[status] || 'gray';
  };

  if (schoolError) {
    return (
      <Alert
        icon={<IconAlertTriangle size={16} />}
        title="Error loading school"
        color="red"
      >
        Failed to load school details. Please try again.
      </Alert>
    );
  }

  if (schoolLoading || !school) {
    return <LoadingOverlay visible />;
  }

  const activeCameras = cameras.filter((c: Camera) => c.status === 'active').length;
  const totalCameras = cameras.length;
  const cameraUptime = totalCameras > 0 ? (activeCameras / totalCameras) * 100 : 0;

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <Group>
          <ActionIcon variant="subtle" onClick={handleBack}>
            <IconArrowLeft size={16} />
          </ActionIcon>
          <div>
            <Title order={2}>{school.name}</Title>
            <Text size="sm" c="dimmed">
              School Details
            </Text>
          </div>
        </Group>
        <Button
          leftSection={<IconEdit size={16} />}
          onClick={handleEdit}
        >
          Edit School
        </Button>
      </Group>

      <Grid>
        {/* Main School Information */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="md">
            {/* Basic Info Card */}
            <Card>
              <Group mb="md">
                <Avatar size="lg" radius="md">
                  <IconSchool size={32} />
                </Avatar>
                <div style={{ flex: 1 }}>
                  <Group justify="space-between" align="flex-start">
                    <div>
                      <Title order={3}>{school.name}</Title>
                      <Text c="dimmed" size="sm">
                        {school.principal_name && `Principal: ${school.principal_name}`}
                      </Text>
                    </div>
                    <Group gap="xs">
                      <Badge
                        color={getSchoolTypeColor(school.school_type || '')}
                        variant="light"
                      >
                        {school.school_type}
                      </Badge>
                      <Badge
                        color={school.active ? 'green' : 'red'}
                        variant="light"
                      >
                        {school.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </Group>
                  </Group>
                </div>
              </Group>

              <Divider mb="md" />

              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {school.email && (
                  <Group>
                    <IconMail size={16} color="gray" />
                    <div>
                      <Text size="xs" c="dimmed">Email</Text>
                      <Text size="sm">{school.email}</Text>
                    </div>
                  </Group>
                )}

                {school.phone && (
                  <Group>
                    <IconPhone size={16} color="gray" />
                    <div>
                      <Text size="xs" c="dimmed">Phone</Text>
                      <Text size="sm">{school.phone}</Text>
                    </div>
                  </Group>
                )}

                {school.address && (
                  <Group align="flex-start" style={{ gridColumn: '1 / -1' }}>
                    <IconMapPin size={16} color="gray" />
                    <div>
                      <Text size="xs" c="dimmed">Address</Text>
                      <Text size="sm">{school.address}</Text>
                    </div>
                  </Group>
                )}

                {school.academic_year && (
                  <Group>
                    <IconCalendar size={16} color="gray" />
                    <div>
                      <Text size="xs" c="dimmed">Academic Year</Text>
                      <Text size="sm">{school.academic_year}</Text>
                    </div>
                  </Group>
                )}
              </SimpleGrid>
            </Card>

            {/* System Health */}
            <Card>
              <Title order={4} mb="md">System Health</Title>
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                <div>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm">Camera Uptime</Text>
                    <Text size="sm" fw={500}>
                      {cameraUptime.toFixed(1)}%
                    </Text>
                  </Group>
                  <Progress
                    value={cameraUptime}
                    color={cameraUptime > 90 ? 'green' : cameraUptime > 70 ? 'yellow' : 'red'}
                    size="sm"
                  />
                </div>

                <div>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm">Active Cameras</Text>
                    <Text size="sm" fw={500}>
                      {activeCameras}/{totalCameras}
                    </Text>
                  </Group>
                  <Progress
                    value={totalCameras > 0 ? (activeCameras / totalCameras) * 100 : 0}
                    color="blue"
                    size="sm"
                  />
                </div>

                <div>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm">Last Updated</Text>
                    <Text size="sm" fw={500}>
                      <IconClock size={12} style={{ marginRight: 4 }} />
                      Just now
                    </Text>
                  </Group>
                </div>
              </SimpleGrid>
            </Card>

            {/* Recent Students */}
            <Card>
              <Group justify="space-between" mb="md">
                <Title order={4}>Recent Students</Title>
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => navigate(`/students?school_id=${id}`)}
                >
                  View All
                </Button>
              </Group>

              {studentsLoading ? (
                <LoadingOverlay visible />
              ) : students.length > 0 ? (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Student</Table.Th>
                      <Table.Th>Class</Table.Th>
                      <Table.Th>Status</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {students.slice(0, 5).map((student: any) => (
                      <Table.Tr key={student.id}>
                        <Table.Td>
                          <Group gap="sm">
                            <Avatar size="sm" radius="xl">
                              {student.first_name?.[0]}{student.last_name?.[0]}
                            </Avatar>
                            <div>
                              <Text size="sm" fw={500}>
                                {student.first_name} {student.last_name}
                              </Text>
                              <Text size="xs" c="dimmed">
                                ID: {student.student_id}
                              </Text>
                            </div>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{student.current_class?.name || 'Not assigned'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={student.active ? 'green' : 'red'}
                            variant="light"
                            size="sm"
                          >
                            {student.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text size="sm" c="dimmed" ta="center" py="md">
                  No students enrolled yet
                </Text>
              )}
            </Card>
          </Stack>
        </Grid.Col>

        {/* Sidebar */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="md">
            {/* Quick Stats */}
            <Card>
              <Title order={4} mb="md">Quick Stats</Title>
              <Stack gap="md">
                <Group justify="space-between">
                  <Group gap="xs">
                    <IconUsers size={16} color="blue" />
                    <Text size="sm">Total Students</Text>
                  </Group>
                  <Text size="sm" fw={500}>
                    {school.student_count || school.students_count || 0}
                  </Text>
                </Group>

                <Group justify="space-between">
                  <Group gap="xs">
                    <IconCamera size={16} color="green" />
                    <Text size="sm">Active Cameras</Text>
                  </Group>
                  <Text size="sm" fw={500}>
                    {activeCameras}
                  </Text>
                </Group>

                <Group justify="space-between">
                  <Group gap="xs">
                    <IconActivity size={16} color="orange" />
                    <Text size="sm">Today's Activities</Text>
                  </Group>
                  <Text size="sm" fw={500}>
                    0
                  </Text>
                </Group>
              </Stack>
            </Card>

            {/* Camera Status */}
            <Card>
              <Title order={4} mb="md">Camera Status</Title>
              {camerasLoading ? (
                <LoadingOverlay visible />
              ) : cameras.length > 0 ? (
                <Stack gap="xs">
                  {cameras.slice(0, 5).map((camera: any) => (
                    <Group key={camera.id} justify="space-between">
                      <Group gap="xs">
                        <IconCamera size={14} />
                        <Text size="sm" truncate>
                          {camera.name}
                        </Text>
                      </Group>
                      <Badge
                        color={getCameraStatusColor(camera.status)}
                        variant="light"
                        size="xs"
                      >
                        {camera.status}
                      </Badge>
                    </Group>
                  ))}
                  {cameras.length > 5 && (
                    <Button
                      variant="subtle"
                      size="xs"
                      onClick={() => navigate(`/cameras?school_id=${id}`)}
                    >
                      View All ({cameras.length})
                    </Button>
                  )}
                </Stack>
              ) : (
                <Text size="sm" c="dimmed" ta="center">
                  No cameras configured
                </Text>
              )}
            </Card>

            {/* Recent Activity */}
            <Card>
              <Title order={4} mb="md">Recent Activity</Title>
              <Stack gap="xs">
                <Group>
                  <IconCheck size={14} color="green" />
                  <Text size="xs" c="dimmed">
                    School information updated
                  </Text>
                </Group>
                <Text size="xs" c="dimmed" ta="center" py="md">
                  No recent activity
                </Text>
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export default SchoolDetail;
